import { Ban } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link, Navigate, type Location } from 'react-router'
import { Button, EmptyState } from '@/components/ui'
import { NotFoundState } from '@/features/errors/NotFoundState'
import { useAuth, useMembership } from '@/lib/auth'
import { useCommunity, usePageviewPing } from '@/lib/community'
import { t } from '@/lib/i18n'
import { CommunityTabs } from './CommunityTabs'
import { GlobalBanners } from './GlobalBanners'
import { canBrowseCommunity, mustRedirectToAbout, showBannedState } from './shell-utils'
import { MultiSlot, SingleSlot } from './slots'

/** Community chrome: tab row, community banners, and the 1080 px content container. */
export function CommunityLayout({ slug, location, children }: { slug: string; location: Location; children: ReactNode }) {
  const { community, notFound } = useCommunity(slug, { followRedirects: true })
  const { isMember, isBanned } = useMembership(slug)
  usePageviewPing(slug, isMember)
  const { isPlatformAdmin } = useAuth()
  const realSlug = community?.slug ?? slug

  if (notFound) {
    return (
      <div className="mx-auto w-full max-w-[1080px] px-4 pt-10 pb-16 lg:px-6">
        <NotFoundState />
      </div>
    )
  }

  if (showBannedState(community, { bannedInMe: isBanned, pathname: location.pathname, slug })) {
    return (
      <div className="mx-auto w-full max-w-[1080px] px-4 pt-10 pb-16 lg:px-6">
        <EmptyState
          icon={Ban}
          tone="danger"
          title={t('You no longer have access to this community')}
          description={t('An admin removed your access to this community.')}
          action={
            <Button asChild variant="primary">
              <Link to="/">{t('Go to the home page')}</Link>
            </Button>
          }
        />
      </div>
    )
  }

  if (mustRedirectToAbout(community, { isMember, isPlatformAdmin, pathname: location.pathname, slug })) {
    return <Navigate to={`/${realSlug}/about`} replace />
  }

  // A private community's visitors only reach About, so every tab would bounce back there: no tab row until they
  // can see the content (Skool plus-about; Q-FAM-05). The top bar drops its search for them too.
  const browsable = canBrowseCommunity(community, { isMember, isPlatformAdmin })

  return (
    <>
      {browsable ? <CommunityTabs slug={slug} community={community} pathname={location.pathname} /> : null}
      <GlobalBanners />
      <SingleSlot name="BillingBanner" props={{ slug: realSlug }} />
      <SingleSlot name="CheckoutSuccessWatcher" props={{ slug: realSlug }} />
      <MultiSlot name="CommunityBanner" props={{ slug: realSlug, community }} />
      <div className="mx-auto w-full max-w-[1080px] px-4 pt-6 pb-24 md:pb-10 lg:px-6">{children}</div>
    </>
  )
}
